import { useState } from "react";
import { Link } from "react-router-dom";
import axios from "axios";
import { API } from "../App";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { toast } from "sonner";
import { ArrowLeft, Mail, Key, Copy, ExternalLink } from "lucide-react";

export default function ForgotPassword() {
  const [email, setEmail] = useState("");
  const [loading, setLoading] = useState(false);
  const [resetData, setResetData] = useState(null);

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const response = await axios.post(`${API}/auth/forgot-password`, { email });
      
      if (response.data.demo_mode && response.data.reset_token) {
        setResetData(response.data);
        toast.success("Reset-link genereret!");
      } else {
        toast.success(response.data.message);
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || "Fejl ved anmodning");
    } finally {
      setLoading(false);
    }
  };

  const copyToken = () => {
    if (resetData?.reset_token) {
      navigator.clipboard.writeText(resetData.reset_token);
      toast.success("Token kopieret til udklipsholder!");
    }
  };

  const copyUrl = () => {
    if (resetData?.reset_url) {
      const fullUrl = window.location.origin + resetData.reset_url;
      navigator.clipboard.writeText(fullUrl);
      toast.success("URL kopieret til udklipsholder!");
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center p-4" 
         style={{backgroundImage: 'url(https://images.unsplash.com/photo-1761477423581-c470874aef50?q=80&w=2000&auto=format&fit=crop)', 
                backgroundSize: 'cover', backgroundPosition: 'center'}}>
      <div className="absolute inset-0 bg-black/60 backdrop-blur-sm"></div>
      
      <div className="relative w-full max-w-md">
        <div className="bg-black/80 backdrop-blur-md border border-white/10 rounded-lg shadow-2xl p-8">
          <Link 
            to="/" 
            className="flex items-center gap-2 text-sm text-muted-foreground hover:text-primary transition-colors mb-6"
            data-testid="back-to-login-link"
          >
            <ArrowLeft className="h-4 w-4" />
            Tilbage til login
          </Link>

          <div className="flex items-center gap-3 mb-6">
            <Key className="h-8 w-8 text-primary" />
            <h1 className="text-2xl font-bold font-heading uppercase tracking-tight">
              Glemt adgangskode
            </h1>
          </div>

          {!resetData ? (
            <>
              <p className="text-muted-foreground text-sm mb-6">
                Indtast din email-adresse, så sender vi dig et link til at nulstille din adgangskode.
              </p>

              <form onSubmit={handleSubmit} className="space-y-4" data-testid="forgot-password-form">
                <div className="space-y-2">
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider font-mono">
                    Email
                  </Label>
                  <Input
                    id="email"
                    data-testid="forgot-email-input"
                    type="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    required
                    placeholder="din@email.dk"
                    className="bg-input/50 border-input focus:border-primary focus:ring-1 focus:ring-primary h-10 font-mono"
                  />
                </div>

                <Button
                  data-testid="forgot-submit-button"
                  type="submit"
                  disabled={loading}
                  className="w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_10px_rgba(225,255,47,0.2)] font-semibold uppercase tracking-wide text-xs h-10 rounded-md transition-all active:scale-95"
                >
                  <Mail className="h-4 w-4 mr-2" />
                  {loading ? "Sender..." : "Send reset-link"}
                </Button>
              </form>
            </>
          ) : (
            <div className="space-y-6" data-testid="reset-token-display">
              <div className="p-4 bg-yellow-500/10 border border-yellow-500/30 rounded-lg">
                <p className="text-yellow-400 text-sm font-medium mb-2">
                  ⚠️ Demo-tilstand
                </p>
                <p className="text-xs text-muted-foreground">
                  I produktion ville dette link blive sendt via email. 
                  I demo-tilstand vises det her direkte.
                </p>
              </div>

              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                  Reset Token (udløber om {resetData.expires_in_minutes} minutter)
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={resetData.reset_token}
                    readOnly
                    className="bg-input/30 border-input font-mono text-xs"
                    data-testid="reset-token-value"
                  />
                  <Button
                    onClick={copyToken}
                    variant="outline"
                    size="icon"
                    className="flex-shrink-0"
                    data-testid="copy-token-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <div className="space-y-3">
                <Label className="text-xs uppercase tracking-wider font-mono text-muted-foreground">
                  Reset URL
                </Label>
                <div className="flex gap-2">
                  <Input
                    value={window.location.origin + resetData.reset_url}
                    readOnly
                    className="bg-input/30 border-input font-mono text-xs"
                    data-testid="reset-url-value"
                  />
                  <Button
                    onClick={copyUrl}
                    variant="outline"
                    size="icon"
                    className="flex-shrink-0"
                    data-testid="copy-url-btn"
                  >
                    <Copy className="h-4 w-4" />
                  </Button>
                </div>
              </div>

              <Link
                to={resetData.reset_url}
                data-testid="go-to-reset-link"
                className="flex items-center justify-center gap-2 w-full bg-primary text-primary-foreground hover:bg-primary/90 shadow-[0_0_10px_rgba(225,255,47,0.2)] font-semibold uppercase tracking-wide text-xs h-10 rounded-md transition-all active:scale-95"
              >
                <ExternalLink className="h-4 w-4" />
                Gå til nulstilling
              </Link>

              <Button
                onClick={() => setResetData(null)}
                variant="outline"
                className="w-full"
                data-testid="try-another-email-btn"
              >
                Prøv en anden email
              </Button>
            </div>
          )}
        </div>
      </div>
    </div>
  );
}
